import { apiRequest } from '../lib/api-client';

/**
 * Unofficial WhatsApp sessions (context.md §68).
 *
 * Experimental, off for every plan, and granted only by an explicit override — so every one of
 * these calls can answer 403, and the screen treats that as "not available here" rather than as a
 * failure.
 */

export type SessionStatus =
  'DISCONNECTED' | 'PAIRING' | 'CONNECTED' | 'RECONNECTING' | 'LOGGED_OUT' | 'BANNED' | 'FAILED';

export type SessionHealth = 'HEALTHY' | 'STALE' | 'PAIRING' | 'DOWN' | 'BLOCKED';

export interface SessionView {
  id: string;
  channel_id: string;
  status: SessionStatus;
  status_detail: string | null;
  /**
   * What the session actually is, rather than what the row claims.
   *
   * `CONNECTED` is a claim; `HEALTHY` means the engine answered recently. A session that has gone
   * quiet reports `STALE`, because a tenant discovering that from a customer complaint is the
   * failure this distinction exists to prevent.
   */
  health: SessionHealth;
  /**
   * Which half of pairing this is: a code waiting to be scanned, or a phone that already scanned
   * and a WhatsApp account being copied across. `status` alone says `PAIRING` for both, and the
   * two need completely different words on screen.
   */
  stage: 'AWAITING_SCAN' | 'SYNCING' | null;
  phone_number: string | null;
  display_name: string | null;
  /** Shown once, short-lived. It is a credential: anybody who sees it can attach a device. */
  pairing_code: string | null;
  pairing_expires_at: string | null;
  last_seen_at: string | null;
  connected_at: string | null;
  reconnect_count: number;
}

/**
 * `local` is the in-process double every development machine runs on: it pairs instantly and its
 * code is a placeholder no phone can read. `remote` is the session service, which returns a real
 * WhatsApp payload. The screen needs the difference, because drawing a placeholder as a QR code
 * sends somebody to scan a square that can never work.
 */
export type WhatsAppWebEngine = 'local' | 'remote' | string;

export function listSessions(): Promise<{ sessions: SessionView[]; engine: WhatsAppWebEngine }> {
  return apiRequest('/v1/whatsapp-web/sessions');
}

export function pairChannel(channelId: string): Promise<SessionView> {
  return apiRequest(`/v1/whatsapp-web/channels/${channelId}/pair`, { method: 'POST' });
}

export interface ImportResult {
  found: number;
  created: number;
  existing: number;
  skipped: number;
  truncated: boolean;
}

/** Brings the paired account's address book into the CRM. Never overwrites what is already there. */
export function importContacts(channelId: string): Promise<ImportResult> {
  return apiRequest(`/v1/whatsapp-web/channels/${channelId}/contacts/import`, { method: 'POST' });
}

export function disconnectChannel(channelId: string): Promise<void> {
  return apiRequest(`/v1/whatsapp-web/channels/${channelId}/disconnect`, { method: 'POST' });
}
